import React, { useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { prayerUnits as prayerUnitsData } from '../data/prayerUnits';

const PrayerUnits = () => {
  const { t } = useLanguage();
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const localizedUnits = useMemo(() => (
    prayerUnitsData.map((unit) => ({
      id: unit.id,
      translationKey: unit.translationKey,
      name: t(`${unit.translationKey}.name`),
      description: t(`${unit.translationKey}.description`)
    }))
  ), [t]);

  const handleUnitClick = (unit) => {
    setSelectedUnit(unit);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedUnit(null);
  };

  return (
    <section id="prayer-units" className="section-padding bg-gray-50">
      <div className="container-max">
        <div className="max-w-5xl mx-auto">
          {/* Section Title */}
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {t('prayerUnits.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('prayerUnits.subtitle')}
            </p>
          </div>
          
          {/* Prayer Units Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {localizedUnits.map((unit) => (
              <div 
                key={unit.id}
                onClick={() => handleUnitClick(unit)}
                className="group bg-white rounded-xl p-5 sm:p-6 text-center border border-gray-200 shadow-sm hover:shadow-lg hover:border-purple-300 transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-600 transition-colors">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight">
                  {unit.name}
                </h3>
                <p className="text-sm text-gray-500 mt-2">
                  {t('prayerUnits.clickForInfo')}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Popup Modal */}
      {isPopupOpen && selectedUnit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-sm sm:max-w-md w-full mx-2 sm:mx-4 relative max-h-[90vh] overflow-y-auto">
            {/* Close Button */}
            <button
              onClick={closePopup}
              className="absolute top-2 sm:top-4 right-2 sm:right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Modal Content */}
            <div className="p-4 sm:p-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 text-center leading-tight">
                {selectedUnit.name}
              </h3>
              
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed text-center">
                {selectedUnit.description}
              </p>
              
              <div className="mt-4 sm:mt-6 flex justify-center">
                <button
                  onClick={closePopup}
                  className="btn-primary px-4 sm:px-6 py-2 text-sm sm:text-base"
                >
                  {t('prayerUnits.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PrayerUnits; 